/**
 * Utilities for working with timezones
 */

/**
 * Get the timezone abbreviation for a given date (e.g., "PST", "CST", "EST")
 *
 * @param date - The date to get the timezone abbreviation for
 * @returns The timezone abbreviation (e.g., "PST", "CDT", "GMT+1") or undefined if not available
 *
 * @example
 * ```typescript
 * const tz = getTimeZoneAbbreviation(new Date());
 * // In Pacific timezone: "PST" or "PDT" (depending on DST)
 * // In Central timezone: "CST" or "CDT"
 * // In Eastern timezone: "EST" or "EDT"
 * ```
 *
 * Note: This automatically handles Daylight Saving Time (DST).
 * - Pacific: "PST" in winter, "PDT" in summer
 * - Central: "CST" in winter, "CDT" in summer
 * - Eastern: "EST" in winter, "EDT" in summer
 */
export function getTimeZoneAbbreviation(date: Date | number): string | undefined {
  return new Intl.DateTimeFormat("en-US", {
    timeZoneName: "shortGeneric",
  })
    .formatToParts(date)
    .find((part) => part.type === "timeZoneName")?.value;
}

/**
 * Get the full timezone name for a given date (e.g., "Pacific Standard Time")
 *
 * @param date - The date to get the timezone name for
 * @returns The full timezone name or undefined if not available
 *
 * @example
 * ```typescript
 * const tz = getTimeZoneName(new Date());
 * // "Pacific Standard Time" or "Pacific Daylight Time"
 * ```
 */
export function getTimeZoneName(date: Date | number): string | undefined {
  return new Intl.DateTimeFormat("en-US", {
    timeZoneName: "longGeneric",
  })
    .formatToParts(date)
    .find((part) => part.type === "timeZoneName")?.value;
}

/**
 * Get the IANA timezone identifier (e.g., "America/Los_Angeles")
 *
 * @returns The IANA timezone identifier
 *
 * @example
 * ```typescript
 * const tz = getTimeZoneIdentifier();
 * // "America/Los_Angeles"
 * // "America/Chicago"
 * // "America/New_York"
 * ```
 */
export function getTimeZoneIdentifier(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
